"use client";

import { ComponentSharedDatasourcesLabelsRegionBriefDatasource, JapanArea } from "@/types/generated";
import { ChevronRightIcon, ChevronUpIcon } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/atoms/button";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";
import ExpandableParagraphRichtext from "@/components/atoms/expandable-paragraph-richtext";

interface DestinationBriefSectionProps {
  destinationAreaData: JapanArea[];
  regionName?: string;
  regionBriefDatasource?: ComponentSharedDatasourcesLabelsRegionBriefDatasource;
}

/**
 * DestinationBriefSection Component
 * Displays the brief title and description for a destination region.
 * Handles expandable text with overflow detection.
 */
export const DestinationBriefSection = ({
  destinationAreaData,
  regionName,
  regionBriefDatasource,
}: DestinationBriefSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMoreButton, setShowReadMoreButton] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const briefDescriptionContent = destinationAreaData[0]?.brief_description;

  // Detect text overflow to decide whether to show "Read More"
  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => {
      setIsExpanded(false);
      setShowReadMoreButton(false);

      if (descriptionRef.current) {
        const el = descriptionRef.current;
        // Safely check overflow after layout calculation
        setShowReadMoreButton(el.scrollHeight > el.clientHeight);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [briefDescriptionContent]);

  if (!regionName || !destinationAreaData || destinationAreaData.length === 0) {
    return null;
  }

  return (
    <div className="mb-10 text-center">
      <div className="mb-4 text-center text-2xl font-[var(--font-semibold)] text-[var(--text-base-primary)] md:text-[24px] lg:mb-6">
        <RichtextRenderer
          customStyle={{
            "": {
              fontWeight: 600,
              color: "inherit",
              fontSize: "inherit",
            },
          }}
          content={destinationAreaData[0].brief_title || ""}
        />
      </div>
      {briefDescriptionContent && (
        <div className="relative">
          <div
            ref={descriptionRef}
            className={`text-center text-sm md:text-base`}
          >
            <ExpandableParagraphRichtext
              paragraph={briefDescriptionContent || ""}
              buttonClassName="!text-sm"
              outerClassName="!text-base"
              lineLimitDesktop={4}
              lineLimitMobile={3}
              lessLinkText={regionBriefDatasource?.hide || undefined}
              moreLinkText={regionBriefDatasource?.read_more || undefined}
              defaultExpanded={false}
              defaultShowButton={false}
            />
          </div>
        </div>
      )}

      {showReadMoreButton && (
        <Button
          className="mt-3"
          variant={"primary"}
          hierarchy={"outline"}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "隠す" : "続きを読む"}
          {isExpanded ? (
            <ChevronUpIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
          ) : (
            <ChevronRightIcon
              className="-mr-1 ml-2 h-5 w-5"
              aria-hidden="true"
            />
          )}
        </Button>
      )}
    </div>
  );
};
